import { Component, ChangeDetectionStrategy, booleanAttribute, input, output } from '@angular/core';
import { IdraButton } from '../button/button';

/**
 * Dialog de confirmation (suppression, révocation…).
 * Composant présentationnel : le parent contrôle l'affichage via @if
 * et réagit aux sorties (confirmed) / (cancelled). Échap et le clic sur
 * le fond annulent également.
 *
 * Usage :
 *  @if (aSupprimer(); as item) {
 *    <idra-confirm-dialog
 *      header="Supprimer la connexion"
 *      [message]="'Supprimer « ' + item.name + ' » ? Cette action est définitive.'"
 *      confirmLabel="Supprimer"
 *      danger
 *      (confirmed)="remove(item)"
 *      (cancelled)="aSupprimer.set(null)"
 *    />
 *  }
 */
@Component({
  selector: 'idra-confirm-dialog',
  imports: [IdraButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'cancelled.emit()',
  },
  styles: `
    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: var(--idra-surface-backdrop);
    }

    .panel {
      width: 100%;
      max-width: 440px;
      padding: 22px 24px;
      background: var(--idra-surface-overlay);
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-lg);
      box-shadow: var(--idra-shadow-lg);
    }

    h3 {
      margin: 0 0 8px;
      font-size: 17px;
      color: var(--idra-text);
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: var(--idra-text-secondary);
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 20px;
    }
  `,
  template: `
    <div class="backdrop" (click)="cancelled.emit()">
      <div
        class="panel"
        role="alertdialog"
        aria-modal="true"
        [attr.aria-label]="header()"
        (click)="$event.stopPropagation()"
      >
        <h3>{{ header() }}</h3>
        <p>{{ message() }}</p>
        <div class="footer">
          <button idra-button type="button" variant="secondary" (click)="cancelled.emit()">
            {{ cancelLabel() }}
          </button>
          <button
            idra-button
            type="button"
            class="confirm"
            [variant]="danger() ? 'danger' : 'primary'"
            (click)="confirmed.emit()"
          >
            {{ confirmLabel() }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class IdraConfirmDialog {
  readonly header = input.required<string>();
  readonly message = input.required<string>();
  readonly confirmLabel = input('Confirmer');
  readonly cancelLabel = input('Annuler');
  /** Variante danger : bouton de confirmation rouge (suppression, révocation). */
  readonly danger = input(false, { transform: booleanAttribute });

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();
}
